/*
  Подключение линии связи: доступы к вашему оператору, под которыми Арбитр ЖКХ
  принимает входящие звонки должников (§7.8.1).

  Номер и учётную запись оформляете вы на своё юридическое лицо - своих номеров
  Арбитр ЖКХ не выдаёт. Поэтому форма спрашивает то же, что написано в кабинете
  вашего оператора связи, и ничего сверх того.

  🔴 Пароль сюда не возвращается никогда - ни при открытии страницы, ни после
  сохранения. Показанный однажды, он попадает в снимок экрана и в историю
  браузера. Пустое поле пароля при заполненных остальных означает «пароль задан
  и не меняется»; чтобы заменить - введите новый.

  Чего здесь нет намеренно: выбора способа подключения. Сейчас поддержан один -
  вы даёте данные своего оператора, и Арбитр ЖКХ регистрируется у него
  (решение владельца 14.09.2026: остальные способы позже).
*/

import { useState } from 'react'

import { fieldErrors } from '../api/client.js'
import { dateTime } from '../api/format.js'
import { LockedForm } from './AccessContext.jsx'
import { Field, Tag } from './Common.jsx'

const FIELDS = [
  {
    key: 'provider',
    label: 'Оператор связи',
    width: 'm',
    hint: 'Название компании, у которой куплен номер - для разбора, если линия отвалится.',
  },
  {
    key: 'did_number',
    label: 'Номер, с которого идут звонки',
    width: 's',
    hint: 'Его увидит должник. Номер принадлежит вам - так требует закон о взыскании.',
  },
  { key: 'sip_host', label: 'Адрес сервера оператора', width: 'm' },
  { key: 'sip_port', label: 'Порт', width: 'xs', hint: 'Обычно 5060.' },
  { key: 'sip_user', label: 'Логин', width: 's' },
]

export default function SettingsVoiceChannel({ channel, onSave, error }) {
  /*
    Пароль - единственное управляемое поле формы: после удачного сохранения оно
    очищается. Оставленные в нём точки читаются как «пароль показан», хотя это
    всего лишь то, что человек сам ввёл минуту назад. При отказе сервера
    введённое сохраняется - набирать пароль заново из-за чужой ошибки незачем.
  */
  const [parolPolya, setParolPolya] = useState('')

  /*
    Какое поле сервер не принял и почему (25.09.2026). Разбор приходит с
    сервера: свои правила проверки кабинет не заводит, иначе их пришлось бы
    держать в двух местах сразу, и первая же правка схемы их развела бы.

    Отметка снимается сама: страница гасит ошибку перед КАЖДЫМ сохранением, и
    отметка от прошлой попытки не висит над полем, которое уже исправили.
  */
  const otkaz = fieldErrors(error)

  return (
    <>
      <Readiness channel={channel} />

      <LockedForm
        onSubmit={async (event) => {
          event.preventDefault()
          const form = new FormData(event.currentTarget)
          const parol = parolPolya.trim()
          const port = String(form.get('sip_port') || '').trim()
          const sohranyono = await onSave({
            mode: 'own_sip',
            provider: form.get('provider') || null,
            did_number: form.get('did_number') || null,
            sip_host: form.get('sip_host') || null,
            sip_port: port ? Number(port) : null,
            sip_user: form.get('sip_user') || null,
            // Пустое поле означает «не меняли»: сервер отличает это от
            // «стереть», потому что стирать пароль незачем - линию выключают
            // целиком, а не оставляют без пароля.
            sip_password: parol || null,
          })
          if (sohranyono) setParolPolya('')
        }}
      >
        {FIELDS.map(({ key, label, width, hint }) => (
          <Field
            key={key}
            label={label}
            width={width}
            hint={hint}
            error={otkaz[key]}
            errorId={`voice-error-${key}`}
          >
            <input
              className="field__input"
              name={key}
              defaultValue={channel[key] ?? ''}
              aria-invalid={otkaz[key] ? 'true' : undefined}
              aria-describedby={otkaz[key] ? `voice-error-${key}` : undefined}
            />
          </Field>
        ))}

        <Field
          label="Пароль"
          width="s"
          error={otkaz.sip_password}
          errorId="voice-error-sip_password"
          hint={
            channel.has_password
              ? 'Пароль задан. Оставьте пустым, если меняете только остальное.'
              : 'Возьмите его в кабинете оператора - раздел «SIP-аккаунт» или «Подключение оборудования».'
          }
        >
          <input
            className="field__input"
            name="sip_password"
            type="password"
            autoComplete="new-password"
            value={parolPolya}
            onChange={(event) => setParolPolya(event.target.value)}
            placeholder={channel.has_password ? 'не меняется' : ''}
            aria-invalid={otkaz.sip_password ? 'true' : undefined}
            aria-describedby={otkaz.sip_password ? 'voice-error-sip_password' : undefined}
          />
        </Field>

        <button className="button" type="submit">
          Сохранить линию
        </button>
      </LockedForm>
    </>
  )
}

/*
  Состояние линии: готова ли она принимать звонки и что мешает.

  Состояние регистрации знает только станция, поэтому здесь показывается то, что
  она сообщила последним отчётом, вместе со временем: «готово» без времени
  проверки читается как «готово прямо сейчас», а это может быть вчерашняя
  правда.
*/
function Readiness({ channel }) {
  /*
    Пока линии нет вовсе, сервер отвечает «подключите канал в настройках
    организации» - и это верно на экране коммуникаций, откуда человека сюда и
    отправляют. Здесь он уже стоит в настройках, и та же фраза отправляет его
    туда, где он находится (осмотр 14.09.2026). Поэтому в самой форме текст
    свой, а серверный остаётся там, где он к месту.
  */
  if (!channel.mode) {
    return (
      <div className="notice notice--warn">
        <div className="notice__what">Линия связи ещё не подключена.</div>
        <div className="notice__do">
          Заполните поля ниже данными вашего оператора связи - их выдают в его личном кабинете,
          раздел «SIP-аккаунт» или «Подключение оборудования». Пока линия не подключена, входящие
          звонки должников до Арбитра ЖКХ не доходят.
        </div>
      </div>
    )
  }

  if (channel.missing.length > 0) {
    return (
      <div className="notice notice--warn">
        <div className="notice__what">Не заполнено: {channel.missing.join(', ')}.</div>
        <div className="notice__do">{channel.instruction}</div>
      </div>
    )
  }

  return (
    <>
      {channel.health === 'failing' ? (
        <div className="notice notice--warn">
          <div className="notice__what">
            Оператор не подтверждает подключение{channel.last_error ? `: ${channel.last_error}` : ''}.
          </div>
          <div className="notice__do">
            Проверьте логин и пароль в кабинете оператора. Пока линия не поднялась, входящие
            звонки должников идут мимо Арбитра ЖКХ.
          </div>
        </div>
      ) : (
        <Tag kind="calm">линия подключена</Tag>
      )}

      {/*
        Класс `muted`, а не `hint`: последний - это кружок «как?» шириной
        восемнадцать пикселей, и текст в нём сжимается в столбик поверх метки
        состояния. Найдено осмотром 14.09.2026, на снимке видно сразу.
      */}
      <div className="muted">
        {channel.line_name ? `Служебное имя линии: ${channel.line_name}. ` : ''}
        {channel.checked_at
          ? `Состояние проверено ${dateTime(channel.checked_at)}.`
          : 'Состояние ещё не проверялось - оно появится в течение минуты после сохранения.'}
      </div>
    </>
  )
}
